import { Fragment, useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Redirect, useLocation } from 'react-router-dom';
import { nanoid } from 'nanoid';
import axiosInstance from '../../../services/axios/axios';
import { WrapImsPacket } from '../../../store/utility';
import { DataTable } from '../../../components/UI/MDC/DataTable/DataTable';
import {
	Headline6,
	Subtitle2,
	Body1,
} from '../../../components/UI/MDC/Typography/Typography';
import { Button } from '@upmc/ims.component-library';
import WithLoadingHandler from '../../../hoc/withLoadingHandler/withLoadingHandler';
import { CSS_CLASSES } from './_constants';
import { ACTION_TYPES } from '../../../components/UI/MDC/DataTable/_types';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Card, { Actions } from '../../../components/UI/MDC/Card/Card';
import { RequestTypes } from '../../../constants/enums';
import { UserContext } from 'context/User/UserContext';
import Icon from '../../../components/UI/MDC/Icon/Icon';
import { deepCloneObject } from '../../../store/utility';
import Base from '../../Base/Base';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import { raiseErrorBanner } from '../../../components/MessageDispatch/messageDispatch';
import { clearMessages } from '../../../store/ancillary/actions';

import classes from './ModifyServiceAccount.module.scss';

const ModifyServiceAccount = () => {
	const [sDescription, setDescription] = useState();
	const [sValidDescription, setValidDescription] = useState(true);
	const [sOpenClinicalModal, setOpenClinicalModal] = useState(false); //Boolean to show clinical application request message for non-employees
	const [sCartModal, setCartModal] = useState(false); //Boolean to show cart modal
	const [sPendingModal, setPendingModal] = useState(false); //Boolean to show pending request information
	const [sIsNonEmployee, setIsNonEmployee] = useState(false); // boolean for non-employee status
	const [
		sUsersServiceAccountApplications,
		setUsersServiceAccountApplications,
	] = useState([]);
	const [sIsLoading, setIsLoading] = useState(false); // boolean for validation loading animation
	const [sIsSubmitting, setIsSubmitting] = useState(false); // boolean for submission loading animation
	const [sWasSubmitted, setWasSubmitted] = useState(false); // boolean for tracking validation
	const [sRequestTrackingNumber, setRequestTrackingNumber] = useState();
	const dispatch = useDispatch();
	const [sCartSelections, setCartSelections] = useState([]);
	const [sOtherJustification, setOtherJustification] = useState();
	const [sClinicalJustifications, setClinicalJustifications] = useState();
	const [sClinicalJustificationSelection, setClinicalJustificationSelection] =
		useState();
	const [sShowFilter, setShowFilter] = useState(false); // Used to filter applications
	const [sfilter, setFilter] = useState('');
	const [sPendingTN, setPendingTN] = useState();
	const [sPendingStatus, setPendingStatus] = useState();
	const [sPendingRequestor, setPendingRequestor] = useState();
	const [sPendingRequestedDate, setPendingRequestedDate] = useState();
	const [sPendingAppName, setPendingAppName] = useState();
	const [sSingleWarningMessage, setSingleWarningMessage] = useState(false); //Boolean to show warning message when revoking two network account
	const [sDoubleWarningMessage, setDoubleWarningMessage] = useState(false); //Boolean to show warning message when revoking two network account
	const [sEmailWarningMessage, setEmailWarningMessage] = useState(false); //Boolean to show warning message when revoking an email account for a shared inbox
	const [sEmailAddWarningMessage, setEmailAddWarningMessage] =
		useState(false); //Boolean to show warning message when revoking an email account for a shared inbox
	const [sEmailDoubleWarningMessage, setEmailDoubleWarningMessage] =
		useState(false); //Boolean to show warning message when revoking an email account for a shared inbox
	const [sDoubleNetwork, setDoubleNetwork] = useState(false); //Boolean to determine if account has two network applications
	const [sSharedInbox, setSharedInbox] = useState(false); //Boolean to determine if account is a shared inbox
	const [sAccountEmail, setAccountEmail] = useState(); //Email account associated with the shared inbox

	/**
	 * @type {import('context/User/UserContext').ContextUser}
	 */
	const contextUser = useContext(UserContext);

	//Application Header info
	const rowHeaders = ['Name'];

	//Cart Header info
	const rowCartHeaders = ['Name', 'Action'];

	//Icon legend used to describe action cells
	const iconLegend = [
		{
			BindTo: 1,
			ValuePrimary: 'Modify Application Access',
		},
		{
			BindTo: 0,
			ValuePrimary: 'Revoke Application Access',
		},
		{
			BindTo: 2,
			ValuePrimary: 'Add Application Access',
			ValueSecondary: 'Deselect',
		},
		{
			BindTo: 3,
			ValuePrimary: 'Pending Application Request',
		},
		{
			BindTo: 4,
			ValuePrimary: 'Reactivate Application Access',
		},
	];

	//#region state parameters
	/**
	 * User specified parameter(s) from state.
	 */
	const location = useLocation();
	const userName = location.state.User;
	const userDepartment = location.state.Department;
	const userHospital = location.state.Hospital;
	const userEmail = location.state.Email;
	const userNetworkID = location.state.Network_ID;
	const userESO_FID = location.state.esoFiId;
	const accountType = {
		ID: location.state.Account_Type,
		Name: location.state.Description,
	};
	const userType = location.state.User_Type;
	const userAccountType = location.state.User_Account_Type;
	const requestType = location.state.REQUEST_TYPE;
	const serviceAccountFamiliarESOID = location.state.Familiar_ESO_ID;
	//#endregion

	useEffect(() => {
		// Filtering the applications that match the search criterea
		const filteredApplications = deepCloneObject(
			sUsersServiceAccountApplications,
		);
		if (sfilter) {
			filteredApplications.forEach(app => {
				app.FilteredOut =
					!app.Cells.Name.Value.toLowerCase().includes(sfilter);
			});
		} else {
			filteredApplications.forEach(app => {
				app.FilteredOut = false;
			});
		}
		setUsersServiceAccountApplications(filteredApplications);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sfilter]);

	/**
	 * Effect on component mount. Queries the user's service account information and performs initial page population accordingly.
	 * @returns void. No clean-up performed.
	 */
	useEffect(() => {
		dispatch(clearMessages());
		setIsLoading(true);

		let payload = {
			F_ESO_NTID: userNetworkID,
			SubUserType: accountType.ID,
			F_ESO_ID: userESO_FID,
		};

		axiosInstance
			.post('api/ModifyServiceAccount/Populate', WrapImsPacket(payload))
			.then(response => {
				setDescription(response.data.Description);
				setClinicalJustifications(
					response.data.Clinical_Justifications,
				);
				let rowObject = {};
				let accounts = {};
				const payload = response.data.Applications;
				// Checking if service account has associated applications.
				if (payload !== null) {
					accounts = payload.map(row => {
						const validation = {
							Key: String(true),
							Value: null,
						};

						// Check to see which applications are associated with the selected service account
						switch (row.State) {
							case 'Add':
								rowObject = {
									ActionCells: [
										{
											BindTo: 2,
											Key: nanoid(),
											Text: 'Add Application Access',
											Disabled: false,
										},
									],
									Key: nanoid(),
									Cells: {
										Name: {
											Value: row.Name,
											Validation: validation,
										},
										Key: {
											Value: row.Key,
										},
										Action: {
											Value: '',
											Validation: validation,
										},
									},
									AdditionalInfo: {
										PatientData: row.Patient_Data,
									},
									Index: row.Index,
									AddedToCart: false,
								};
								return rowObject;
							case 'Modify':
								rowObject = {
									ActionCells: [
										{
											BindTo: 1,
											Key: nanoid(),
											Text: 'Modify Application Access',
											Disabled: false,
										},
										{
											BindTo: 0,
											Key: nanoid(),
											Text: 'Revoke Application Access',
											Disabled: false,
										},
									],
									Key: nanoid(),
									Cells: {
										Name: {
											Value: row.Name,
											Validation: validation,
										},
										Key: {
											Value: row.Key,
										},
										Action: {
											Value: '',
											Validation: validation,
										},
									},
									AdditionalInfo: {
										PatientData: row.Patient_Data,
									},
									Index: row.Index,
									AddedToCart: false,
								};

								//Disable revoke action for Network, EU Network, and ADGroups
								if (rowObject.Cells.Key.Value === '158') {
									rowObject.ActionCells[1].Disabled = true;
								}

								return rowObject;
							case 'Pending':
								rowObject = {
									ActionCells: [
										{
											BindTo: 3,
											Key: nanoid(),
											Text: 'Pending Application Request',
											Disabled: false,
										},
									],
									Key: nanoid(),
									Cells: {
										Name: {
											Value: row.Name,
											Validation: validation,
										},
										Key: {
											Value: row.Key,
										},
										Action: {
											Value: '',
											Validation: validation,
										},
									},
									AdditionalInfo: {
										Trackingnumber: row.Tracking_Number,
										Requestor: row.Requestor,
										RequestorESOID: row.Requestor_FamESOID,
										RequestedDate: row.Requested_Date,
										ApplicationName: row.Name,
										Status: row.Status,
									},
									Index: row.Index,
									AddedToCart: false,
								};
								return rowObject;
							case 'Reactivatable':
								rowObject = {
									ActionCells: [
										{
											BindTo: 4,
											Key: nanoid(),
											Text: 'Reactivate Application Access',
											Disabled: false,
										},
									],
									Key: nanoid(),
									Cells: {
										Name: {
											Value: row.Name,
											Validation: validation,
										},
										Key: {
											Value: row.Key,
										},
										Action: {
											Value: '',
											Validation: validation,
										},
									},
									AdditionalInfo: {
										PatientData: row.Patient_Data,
									},
									Index: row.Index,
									AddedToCart: false,
								};
								return rowObject;
							default:
								return rowObject;
						}
					});

					// User can revoke Network or EU Network if they have an account with both applications
					const accountsClone = deepCloneObject(accounts);
					var resultsNetwork = [];
					var resultsEmail = [];
					accountsClone.forEach(obj => {
						if (
							(obj.Cells.Key.Value === '23' &&
								obj.ActionCells.length > 1) ||
							(obj.Cells.Key.Value === '749' &&
								obj.ActionCells.length > 1)
						) {
							resultsNetwork.push(obj);
						}
						if (
							(obj.Cells.Key.Value === '18' ||
								obj.Cells.Key.Value === '750') &&
							(obj.ActionCells.length > 1 ||
								obj.ActionCells[0].Text ===
									'Pending Application Request')
						) {
							resultsEmail.push(obj);
						}
					});
					if (resultsNetwork.length > 1) {
						setDoubleNetwork(true);
						accounts.forEach(obj => {
							if (
								obj.Cells.Key.Value === '23' ||
								obj.Cells.Key.Value === '749'
							) {
								obj.ActionCells[1].Disabled = false;
							}
						});
					}

					// Determining if the account is a shared inbox
					if (accountType.ID === 6) {
						setSharedInbox(true);
						if (resultsEmail.length > 0) {
							setAccountEmail(resultsEmail);
						}
					}

					setUsersServiceAccountApplications(accounts);
					// Allow search function for applications once exceeding a given number of apps
					if (accounts.length > 10) {
						setShowFilter(true);
					}
				}
				// Error message for inactive service account types.
				else {
					const errorMessage = (
						<div>
							Account type: <b>{accountType.Name}</b>
							<br></br>
							<br></br>
							The following service account is no longer using a
							supported service <b>account type</b>. If this
							account is still needed, please contact IMS Support
							to have this account moved to a supported{' '}
							<b>account type</b>. If the account is no longer
							needed please terminate this account.
						</div>
					);
					raiseErrorBanner(errorMessage, dispatch);
				}
			})
			.catch(error => {
				raiseErrorBanner(error, dispatch);
			});
		setIsLoading(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (sUsersServiceAccountApplications) {
			const updatedUsersServiceAccountApplications = deepCloneObject(
				sUsersServiceAccountApplications,
			);
			const cart = updatedUsersServiceAccountApplications
				.filter(a => a.AddedToCart)
				.map(row => {
					const validation = {
						Key: String(true),
						Value: null,
					};
					let rowObject = {
						ActionCells: [
							{
								BindTo: 0,
								Disabled: false,
								Key: nanoid(),
								Text: 'Remove',
							},
						],
						Key: nanoid(),
						Cells: {
							Name: {
								Value: row.Cells.Name.Value,
								Validation: validation,
							},
							Key: {
								Value: row.Cells.Key.Value,
							},
							Action: {
								Value: row.Cells.Action.Value,
								Validation: validation,
							},
						},
						Index: row.Index,
					};
					return rowObject;
				});
			setCartSelections(cart);
		}
	}, [sUsersServiceAccountApplications]);

	/**
	 * Submission click handler. Constructs payload for server-side processing if form valid.
	 * @returns Tracking Number
	 */
	const onSubmissionHandler = async () => {
		if (
			sUsersServiceAccountApplications.filter(a => a.AddedToCart).length >
				0 &&
			!sIsLoading &&
			sDescription.length > 0 &&
			sValidDescription
		) {
			if (sClinicalJustificationSelection) {
				var ClinicalJustificationValue =
					sClinicalJustificationSelection.Value;
			}

			setIsSubmitting(true);
			axiosInstance
				.post(
					'api/ModifyServiceAccount/Submission',
					WrapImsPacket({
						LoggedOnUser: contextUser.userInfo.userProfile.ID,
						Applications: sUsersServiceAccountApplications
							.filter(a => a.AddedToCart)
							.map(app => {
								return {
									ID: app.Cells.Key.Value,
									Name: app.Cells.Name.Value,
									Action: app.Cells.Action.Value,
								};
							}),
						Owners: [
							{
								Cells: {
									Service_Account_Familiar_ESO_ID: {
										Value: serviceAccountFamiliarESOID,
									},
									Owner_Email: {
										Value: '',
									},
									Owner_Network_ID: {
										Value: '',
									},
									Service_Account_Network_ID: {
										Value: userNetworkID,
									},
								},
							},
						],
						Justification: sDescription,
						ClinicalJustification: ClinicalJustificationValue,
						OtherClinicalJustification: sOtherJustification,
						AccountType: accountType,
						UserType: userType,
					}),
				)
				.then(response => {
					setRequestTrackingNumber(
						Object.keys(response.data.payload.WIPHeaderDetails)[0],
					);
				})
				.catch(error => {
					raiseErrorBanner(error, dispatch);
					setIsSubmitting(false);
				});
		}
		setWasSubmitted(true);
	};

	/**
	 * Event handler for cart pop up. Appears when ready to submit.
	 */
	let onCartPopupHandler = () => {
		const cart = sCartSelections.map(row => {
			const validation = {
				Key: String(true),
				Value: null,
			};
			let rowObject = {
				ActionCells: [
					{
						BindTo: 0,
						Disabled: false,
						Key: nanoid(),
						Text: 'Remove',
					},
				],
				Key: nanoid(),
				Cells: {
					Name: {
						Value: row.Cells.Name.Value,
						Validation: validation,
					},
					Key: {
						Value: row.Cells.Key.Value,
					},
					Action: {
						Value: row.Cells.Action.Value,
						Validation: validation,
					},
				},
				Index: row.Index,
			};
			return rowObject;
		});
		setCartSelections(cart);
		setCartModal(true);

		// Showing warning message regarding an attempt to revoke two network accounts
		const cartClone = deepCloneObject(sCartSelections);
		var results = [];
		cartClone.forEach(obj => {
			if (
				(obj.Cells.Key.Value === '23' ||
					obj.Cells.Key.Value === '749') &&
				obj.Cells.Action.Value === 'Revoke'
			) {
				results.push(obj);
			}
		});
		if (results.length > 1) {
			setDoubleWarningMessage(true);
		}

		if (!sDoubleNetwork) {
			// Showing warning message regarding an attempt to revoke one network account
			const cartClone = deepCloneObject(sCartSelections);
			results = [];
			cartClone.forEach(obj => {
				if (
					(obj.Cells.Key.Value === '23' ||
						obj.Cells.Key.Value === '749') &&
					obj.Cells.Action.Value === 'Revoke'
				) {
					results.push(obj);
				}
			});
			if (results.length === 1) {
				setSingleWarningMessage(true);
			}
		}

		if (sSharedInbox) {
			// Showing warning message regarding an attempt to revoke an email account for a shared inbox
			// Showing warning message regarding an attempt to add an email account of a different type for a shared inbox
			// Showing warning message regarding an attempt to add multiple email accounts for a shared inbox
			const cartEmailClone = deepCloneObject(sCartSelections);
			var resultsRevoke = [];
			var resultsAdd = [];
			cartEmailClone.forEach(obj => {
				if (
					(obj.Cells.Key.Value === '18' ||
						obj.Cells.Key.Value === '750') &&
					obj.Cells.Action.Value === 'Revoke'
				) {
					resultsRevoke.push(obj);
				}
				if (
					(obj.Cells.Key.Value === '18' ||
						obj.Cells.Key.Value === '750') &&
					obj.Cells.Action.Value === 'Add'
				) {
					resultsAdd.push(obj);
				}
			});
			if (resultsRevoke.length === 1) {
				setEmailWarningMessage(true);
			}
			if (
				resultsAdd.length === 1 &&
				sAccountEmail &&
				resultsAdd[0].Cells.Name.Value !==
					sAccountEmail[0].Cells.Name.Value
			) {
				setEmailAddWarningMessage(true);
			}
			if (resultsAdd.length > 1) {
				setEmailDoubleWarningMessage(true);
			}
		}
	};

	/**
	Adding/removing items to/from the request page
	*/
	let onRowSelectHandler = (row, action) => {
		setCartModal(false);
		if (row.AdditionalInfo.PatientData === 1 && userAccountType !== 7) {
			setIsNonEmployee(true);
		}

		row.Cells.Action.Value = action;
		var actionText = '';
		var index = null;
		switch (row.Cells.Action.Value) {
			case 'Add':
				actionText = 'Add Application Access';
				index = 0;
				break;
			case 'ModifyAccess':
				actionText = 'Modify Application Access';
				index = 0;
				//Prevents ADGroups applications from being revokable
				if (['158'].includes(row.Cells.Key.Value)) {
					row.ActionCells[1].Disabled = true;
				} else {
					row.ActionCells[1].Disabled = row.ActionCells[index].Checked
						? false
						: true;
				}
				break;
			case 'Revoke':
				actionText = 'Revoke Application Access';
				index = 1;
				row.ActionCells[0].Disabled = row.ActionCells[index].Checked
					? false
					: true;
				break;
			case 'Reactivate':
				actionText = 'Reactivate Application Access';
				index = 0;
				break;
			default:
				break;
		}
		row.ActionCells[index].Checked = !row.ActionCells[index].Checked;
		row.ActionCells[index].Text = row.ActionCells[index].Checked
			? 'Deselect'
			: actionText;
		row.AddedToCart = !row.AddedToCart;

		const updatedUsersServiceAccountApplications = deepCloneObject(
			sUsersServiceAccountApplications,
		);
		updatedUsersServiceAccountApplications[row.Index] = row;
		setUsersServiceAccountApplications(
			updatedUsersServiceAccountApplications,
		);
	};

	/**
	Removing items from the cart pop up
	*/
	let onRowRemoveHandler = row => {
		var actionText = '';
		var index = null;
		const updatedUsersServiceAccountApplications = deepCloneObject(
			sUsersServiceAccountApplications,
		);
		switch (row.Cells.Action.Value) {
			case 'Add':
				actionText = 'Add Application Access';
				index = 0;
				break;
			case 'ModifyAccess':
				actionText = 'Modify Application Access';
				index = 0;
				if (['158'].includes(row.Cells.Key.Value)) {
					updatedUsersServiceAccountApplications[
						row.Index
					].ActionCells[1].Disabled = true;
				} else {
					updatedUsersServiceAccountApplications[
						row.Index
					].ActionCells[1].Disabled = false;
				}
				break;
			case 'Revoke':
				actionText = 'Revoke Application Access';
				index = 1;
				updatedUsersServiceAccountApplications[
					row.Index
				].ActionCells[0].Disabled = false;
				break;
			case 'Reactivate':
				actionText = 'Reactivate Application Access';
				index = 0;
				break;
			default:
				break;
		}

		updatedUsersServiceAccountApplications[row.Index].ActionCells[
			index
		].Checked = false;
		updatedUsersServiceAccountApplications[row.Index].ActionCells[
			index
		].Text = actionText;
		updatedUsersServiceAccountApplications[row.Index].AddedToCart = false;
		setUsersServiceAccountApplications(
			updatedUsersServiceAccountApplications,
		);
		//Resetting the Network message if the service account's network account is removed from the cart
		if (
			(row.Cells.Key.Value === '23' &&
				row.Cells.Action.Value !== 'Add') ||
			(row.Cells.Key.Value === '749' && row.Cells.Action.Value !== 'Add')
		) {
			setDoubleWarningMessage(false);
			setSingleWarningMessage(false);
		}

		if (row.Cells.Key.Value === '18' || row.Cells.Key.Value === '750') {
			setEmailWarningMessage(false);
			setEmailAddWarningMessage(false);
			setEmailDoubleWarningMessage(false);
		}
	};

	/**
	 * Pending Application change handler.
	 */
	let onPendingPopupHandler = row => {
		setPendingTN(row.AdditionalInfo.Trackingnumber);
		setPendingStatus(row.AdditionalInfo.Status);
		setPendingRequestor(row.AdditionalInfo.Requestor);
		setPendingRequestedDate(row.AdditionalInfo.RequestedDate);
		setPendingAppName(row.AdditionalInfo.ApplicationName);

		setPendingModal(true);
	};

	/**
	 * Clinical Justification change handler.
	 */
	const onClinicalJustificationChangeHandler =
		clinicalJustificationSelection => {
			setClinicalJustificationSelection(clinicalJustificationSelection);
			setOtherJustification();
		};

	const onDescriptionTextChangedHandler = e => {
		if (!e.target.value.match('^(?:[A-Za-z0-9 ,-]|)+$')) {
			setValidDescription(false);
		} else {
			setValidDescription(true);
		}
		setDescription(e.target.value);
	};

	const onJustificationTextChangedHandler = e => {
		setOtherJustification(e.target.value);
	};

	/**
	Pop up for non-employees attempting to request for clinical applications
	*/
	let clinicalModal = sOpenClinicalModal && sIsNonEmployee && (
		<Dialog
			open={sOpenClinicalModal}
			onClose={() => setOpenClinicalModal(false)}
		>
			<DialogTitle>Non-employee Clinical Access Requirements</DialogTitle>

			<DialogContent>
				<div className={classes[CSS_CLASSES.WARNING_CLINICAL_CONTENT]}>
					<p>
						Because you are requesting clinical application access
						for a non-employee, you are required to provide a
						justification below.
					</p>
					<Body1>
						<b>
							*Justification (Reason for Clinical app access
							request):
						</b>
					</Body1>
					<select
						value={
							sClinicalJustificationSelection
								? sClinicalJustificationSelection.Key
								: ''
						}
						onChange={e => {
							onClinicalJustificationChangeHandler(
								e.target.value
									? {
											Key: e.target.value,
											Value: e.target.selectedOptions[0]
												.text,
										}
									: null,
							);
						}}
					>
						<option key='0' value=''>
							--Select--
						</option>
						{sClinicalJustifications.map((justification, index) => (
							<option key={index++} value={justification.Key}>
								{justification.Name}
							</option>
						))}
					</select>
					{sClinicalJustificationSelection &&
					sClinicalJustificationSelection.Value === 'Other' ? (
						<>
							<br /> <br />
							<input
								type='text'
								className={CSS_CLASSES.CONTROL}
								placeholder='Please enter a description for "Other"'
								maxLength='200'
								value={sOtherJustification}
								onChange={onJustificationTextChangedHandler}
							/>
						</>
					) : null}
				</div>
			</DialogContent>
			<DialogActions>
				<Button
					variant='text'
					onClick={() => setOpenClinicalModal(false)}
				>
					Cancel
				</Button>
				<Button
					variant='text'
					disabled={
						!sClinicalJustificationSelection ||
						(sClinicalJustificationSelection.Value === 'Other' &&
							!sOtherJustification)
					}
					onClick={() => {
						setOpenClinicalModal(false);
						onSubmissionHandler();
					}}
				>
					Submit
				</Button>
			</DialogActions>
		</Dialog>
	);

	/**
	 * Pending application request pop up.
	 */
	let PendingModal = sPendingModal && (
		<Dialog open={sPendingModal} onClose={() => setPendingModal(false)}>
			<DialogTitle>Pending Request Details</DialogTitle>
			<DialogContent>
				<List dense disablePadding>
					<ListItem dense divider>
						<Subtitle2>
							<b>Tracking Number:</b>&nbsp;{sPendingTN}
						</Subtitle2>
					</ListItem>
					<ListItem dense divider>
						<Subtitle2>
							<b>Application Name:</b>&nbsp;{sPendingAppName}
						</Subtitle2>
					</ListItem>
					<ListItem dense divider>
						<Subtitle2>
							<b>Requestor:</b>&nbsp;{sPendingRequestor}
						</Subtitle2>
					</ListItem>
					<ListItem dense divider>
						<Subtitle2>
							<b>Requested Date:</b>&nbsp;{sPendingRequestedDate}
						</Subtitle2>
					</ListItem>
					<ListItem dense divider>
						<Subtitle2>
							<b>Status:</b>&nbsp;{sPendingStatus}
						</Subtitle2>
					</ListItem>
				</List>
			</DialogContent>
			<DialogActions>
				<Button variant='text' onClick={() => setPendingModal(false)}>
					Close
				</Button>
			</DialogActions>
		</Dialog>
	);

	/**
	Cart view pop up
	*/
	let cartModal = sCartModal &&
		sUsersServiceAccountApplications.filter(a => a.AddedToCart).length >
			0 && (
			<Dialog
				open={sCartModal}
				onClose={() => {
					setCartModal(false);
					setDoubleWarningMessage(false);
					setSingleWarningMessage(false);
					setEmailWarningMessage(false);
					setEmailAddWarningMessage(false);
					setEmailDoubleWarningMessage(false);
				}}
			>
				<DialogTitle>
					Your Cart Items (
					{
						sUsersServiceAccountApplications.filter(
							a => a.AddedToCart,
						).length
					}
					)
				</DialogTitle>
				<DialogContent>
					<div>
						{sDoubleWarningMessage ? (
							<div>
								<Headline6
									className={
										classes[CSS_CLASSES.POPUP_WARNING]
									}
								>
									ATTENTION
								</Headline6>
								<div
									className={
										classes[
											CSS_CLASSES.POPUP_WARNING_CONTENT
										]
									}
								>
									Both UPMC Network and EU Network
									applications cannot be revoked at the same
									time. Only one of these applications can be
									revoked to keep the entire account active.
									If you need to revoke both applications, the
									entire service account will need to be
									terminated in full by navigating to the
									previous page. To continue, please remove
									one.
								</div>
								<br></br>
							</div>
						) : null}
					</div>
					<div>
						{sSingleWarningMessage ? (
							<div>
								<Headline6
									className={
										classes[CSS_CLASSES.POPUP_WARNING]
									}
								>
									ATTENTION
								</Headline6>
								<div
									className={
										classes[
											CSS_CLASSES.POPUP_WARNING_CONTENT
										]
									}
								>
									Network / EU Network cannot be revoked as
									the entire account is dependent on this
									application(s) remaining active. If you need
									to revoke the application, the entire
									service account will need to be terminated
									in full on the previous page.
								</div>
								<br></br>
							</div>
						) : null}
					</div>
					<div>
						{sEmailWarningMessage ? (
							<div>
								<Headline6
									className={
										classes[CSS_CLASSES.POPUP_WARNING]
									}
								>
									ATTENTION
								</Headline6>
								<div
									className={
										classes[
											CSS_CLASSES.POPUP_WARNING_CONTENT
										]
									}
								>
									Email / EU Email cannot be revoked as the
									entire account is dependent on this
									application(s) remaining active. If you need
									to revoke the application, the entire
									service account will need to be terminated
									in full on the previous page.
								</div>
								<br></br>
							</div>
						) : null}
					</div>
					<div>
						{sEmailAddWarningMessage ? (
							<div>
								<Headline6
									className={
										classes[CSS_CLASSES.POPUP_WARNING]
									}
								>
									ATTENTION
								</Headline6>
								<div
									className={
										classes[
											CSS_CLASSES.POPUP_WARNING_CONTENT
										]
									}
								>
									The following Email application cannot be
									added because an Email application is
									already associated with this account. These
									accounts are only permitted to have one
									Email association.
								</div>
								<br></br>
							</div>
						) : null}
						{sEmailDoubleWarningMessage ? (
							<div>
								<Headline6
									className={
										classes[CSS_CLASSES.POPUP_WARNING]
									}
								>
									ATTENTION
								</Headline6>
								<div
									className={
										classes[
											CSS_CLASSES.POPUP_WARNING_CONTENT
										]
									}
								>
									Both Email and Europe Email applications
									cannot be requested for a single service
									account. Only one of these applications can
									be associated with this account. To
									continue, please remove one of these
									applications.
								</div>
								<br></br>
							</div>
						) : null}
					</div>
					<div className={classes[CSS_CLASSES.DATATABLE]}>
						<DataTable
							actions={[
								{
									onChangedHandler: (_, __, row) =>
										onRowRemoveHandler(row),
									Type: ACTION_TYPES.REMOVE,
								},
							]}
							headers={[
								...rowCartHeaders.map(header => {
									return { Value: header };
								}),
							]}
							rows={sCartSelections}
						/>
					</div>
				</DialogContent>
				<DialogActions>
					<Button
						variant='text'
						onClick={() => {
							setCartModal(false);
							setDoubleWarningMessage(false);
							setSingleWarningMessage(false);
							setEmailWarningMessage(false);
							setEmailAddWarningMessage(false);
							setEmailDoubleWarningMessage(false);
						}}
					>
						Back
					</Button>
					<Button
						variant='text'
						disabled={
							sDoubleWarningMessage ||
							sSingleWarningMessage ||
							sEmailWarningMessage ||
							sEmailAddWarningMessage ||
							sEmailDoubleWarningMessage
						}
						onClick={() => {
							setCartModal(false);
							sIsNonEmployee
								? setOpenClinicalModal(true)
								: onSubmissionHandler();
						}}
					>
						Proceed
					</Button>
				</DialogActions>
			</Dialog>
		);

	/**
	 * Clinical application request for non-employees and the corresponding description.
	 */
	let descriptionInput = (
		<Fragment>
			<Headline6> Description* </Headline6>
			{(!sDescription || sDescription.length < 1) && sWasSubmitted ? (
				<Subtitle2 className={classes[CSS_CLASSES.INVALID]}>
					Please enter a description.
				</Subtitle2>
			) : null}
			<input
				type='text'
				className={CSS_CLASSES.CONTROL}
				value={sDescription}
				onChange={onDescriptionTextChangedHandler}
				maxLength='200'
			/>
			{!sValidDescription ? (
				<Subtitle2 className={classes[CSS_CLASSES.INVALID]}>
					No special characters: @,#,$,%, etc.
				</Subtitle2>
			) : null}
		</Fragment>
	);

	/**
	 * Applications available for request.
	 */
	let applicationDataTable = sUsersServiceAccountApplications &&
		!sIsLoading && (
			<div className={classes[CSS_CLASSES.DATATABLE]}>
				<DataTable
					actions={[
						{
							onChangedHandler: (_, __, row) =>
								onRowSelectHandler(row, 'Revoke'),
							Type: ACTION_TYPES.REVOKE_TOGGLE,
						},
						{
							onChangedHandler: (_, __, row) =>
								onRowSelectHandler(row, 'ModifyAccess'),
							Type: ACTION_TYPES.MODIFY_TOGGLE,
						},
						{
							onChangedHandler: (_, __, row) =>
								onRowSelectHandler(row, 'Add'),
							Type: ACTION_TYPES.TOGGLE,
						},
						{
							onChangedHandler: (_, __, row) =>
								onPendingPopupHandler(row),
							Type: ACTION_TYPES.FLAG_TIME,
						},
						{
							onChangedHandler: (_, __, row) =>
								onRowSelectHandler(row, 'Reactivate'),
							Type: ACTION_TYPES.REACTIVATE_TOGGLE,
						},
					]}
					headers={[
						...rowHeaders.map(header => {
							return { Value: header };
						}),
					]}
					iconLegend={iconLegend}
					label='Applications'
					rows={sUsersServiceAccountApplications.filter(a => {
						return !a.FilteredOut;
					})}
					indexed={sShowFilter}
					onTextFieldChangedHandler={e => setFilter(e.target.value)}
				/>
			</div>
		);

	/**
	 * Form body.
	 */
	let Form = !sIsSubmitting && (
		<Fragment>
			{clinicalModal}
			{cartModal}
			{PendingModal}
			<Card className={classes[CSS_CLASSES.PAGE_HEADER]}>
				<br></br>
				<Body1>
					Below are the details for the selected service account:{' '}
					<br></br>
					<b>
						<div className={classes[CSS_CLASSES.USER_NETWORK_ID]}>
							{serviceAccountFamiliarESOID}
							<br></br>
							<br></br>
						</div>
						{userName}
						<br></br>
						{userDepartment}
						<br></br>
						{userHospital}
						<br></br>
						{userEmail}
						<br></br>
					</b>
				</Body1>
				<br></br>
			</Card>
			<WithLoadingHandler
				isLoading={sIsLoading}
				showChildren={!sIsLoading}
			>
				{descriptionInput}
				<br></br>
				<Actions className={classes[CSS_CLASSES.CART]}>
					<Button
						variant='outlined'
						startIcon={<Icon icon='shopping_cart' />}
						onClick={onCartPopupHandler}
						disabled={sIsLoading}
						title='Checkout'
					>
						{
							sUsersServiceAccountApplications.filter(
								a => a.AddedToCart,
							).length
						}
					</Button>
				</Actions>
				{applicationDataTable}
			</WithLoadingHandler>
			<Actions className={classes[CSS_CLASSES.ACTIONS]}>
				<Button
					onClick={onCartPopupHandler}
					disabled={
						sUsersServiceAccountApplications.filter(
							a => a.AddedToCart,
						).length < 1
					}
				>
					Submit
				</Button>
			</Actions>
		</Fragment>
	);

	// Redirect request to app pages when the submission is complete
	return sRequestTrackingNumber ? (
		<div>
			<Redirect
				to={
					'/RRS/Request_ModifyDetail_View.aspx?tn=' +
					sRequestTrackingNumber +
					'&REQUEST_TYPE=' +
					(requestType === 'For_Myself'
						? RequestTypes.SELF_SERVICE_ACCOUNT
						: RequestTypes.FOR_OTHERS_SERVICE_ACCOUNT)
				}
			/>
		</div>
	) : (
		<Base
			title='Modify Service Account'
			helpLink={
				'https://infonet.upmc.com/infonet/ls/content/3298180640090487/service-account-creation-and-management-pdf-1764800010'
			}
			loading={sIsLoading || sIsSubmitting}
		>
			{Form}
		</Base>
	);
};

export default ModifyServiceAccount;
